import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { del, errorMessage, get, post } from "../../api";
import { Alert, Button, Panel, PanelHeader } from "../../components/ui";

/** The Stripe S700's screen, portrait. */
const W = 1080;
const H = 1920;
const PREVIEW = 4; // the on-page frame is a quarter size
const MAX_BYTES = 2 * 1024 * 1024;

type Placement = { zoom: number; x: number; y: number };

/** Draw the picture covering a w × h frame, zoomed and moved (x, y are fractions of the frame). */
function draw(ctx: CanvasRenderingContext2D, img: HTMLImageElement, w: number, h: number, p: Placement) {
  const s = Math.max(w / img.naturalWidth, h / img.naturalHeight) * p.zoom;
  const dw = img.naturalWidth * s;
  const dh = img.naturalHeight * s;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, (w - dw) / 2 + p.x * w, (h - dh) / 2 + p.y * h, dw, dh);
}

/** Keep the frame covered: the picture can't be moved past its edges. */
function clamp(img: HTMLImageElement, p: Placement): Placement {
  const s = Math.max(W / img.naturalWidth, H / img.naturalHeight) * p.zoom;
  const mx = (img.naturalWidth * s - W) / (2 * W);
  const my = (img.naturalHeight * s - H) / (2 * H);
  return { zoom: p.zoom, x: Math.min(mx, Math.max(-mx, p.x)), y: Math.min(my, Math.max(-my, p.y)) };
}

/** The full-size JPEG, as small as needed to stay under Stripe's 2 MB. */
async function render(img: HTMLImageElement, p: Placement): Promise<Blob> {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  draw(c.getContext("2d")!, img, W, H, p);
  for (const q of [0.92, 0.85, 0.75, 0.6]) {
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, "image/jpeg", q));
    if (blob && blob.size < MAX_BYTES) return blob;
  }
  throw new Error("That picture is too detailed to fit the reader. Try another.");
}

/** Settings → Payments: the picture on every card reader's idle screen. */
export function ReaderBackground() {
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ["reader-background"], queryFn: () => get<{ url: string | null }>("/api/card-readers/background") });
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [gif, setGif] = useState<File | null>(null);
  const [place, setPlace] = useState<Placement>({ zoom: 1, x: 0, y: 0 });
  const [notice, setNotice] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const gifUrl = useMemo(() => (gif ? URL.createObjectURL(gif) : null), [gif]);

  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (ctx && img) draw(ctx, img, W / PREVIEW, H / PREVIEW, place);
  }, [img, place]);

  const pick = (file: File | undefined) => {
    setNotice(null);
    save.reset();
    if (!file) return;
    if (file.type === "image/gif") {
      // GIFs go as they are (Stripe fits them to the screen; editing would lose the animation).
      setImg(null);
      setGif(file);
      return;
    }
    setGif(null);
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => (setImg(i), setPlace({ zoom: 1, x: 0, y: 0 }));
    i.src = url;
  };

  const done = (msg: string) => {
    setImg(null);
    setGif(null);
    setNotice(msg);
    void qc.invalidateQueries({ queryKey: ["reader-background"] });
  };
  const save = useMutation({
    mutationFn: async () => {
      let body: Blob;
      let type: string;
      if (gif) {
        if (gif.size > 4 * 1024 * 1024) throw new Error("GIFs must be under 4 MB.");
        body = gif;
        type = "image/gif";
      } else {
        body = await render(img!, place);
        type = "image/jpeg";
      }
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/card-readers/background/upload", { contentType: type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body });
      if (!res.ok) throw new Error("The picture couldn't be uploaded. Try again.");
      await post("/api/card-readers/background", { key: up.key });
    },
    onSuccess: () => done("Sent to the readers. It shows the next time a reader is idle (a minute or two)."),
  });
  const reset = useMutation({ mutationFn: () => del("/api/card-readers/background"), onSuccess: () => done("The readers are back to Stripe's own screen.") });

  const editing = img || gif;
  return (
    <Panel>
      <PanelHeader title="Reader background" description="The picture on every card reader's screen while it waits for the next payment. The bill, tip, and card screens stay Stripe's." />
      <div className="flex flex-col gap-4 px-5 pb-5">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {(save.error ?? reset.error) && <Alert>{errorMessage(save.error ?? reset.error)}</Alert>}
        <div className="flex flex-wrap items-start gap-6">
          {img ? (
            <div className="flex flex-col gap-2">
              <canvas
                ref={canvas}
                width={W / PREVIEW}
                height={H / PREVIEW}
                className="cursor-grab touch-none rounded-xl border border-line shadow-sm"
                onPointerDown={(e) => {
                  drag.current = { x: e.clientX, y: e.clientY };
                  e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onPointerMove={(e) => {
                  if (!drag.current) return;
                  const dx = (e.clientX - drag.current.x) / (W / PREVIEW);
                  const dy = (e.clientY - drag.current.y) / (H / PREVIEW);
                  drag.current = { x: e.clientX, y: e.clientY };
                  setPlace((p) => clamp(img, { ...p, x: p.x + dx, y: p.y + dy }));
                }}
                onPointerUp={() => (drag.current = null)}
              />
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                Zoom
                <input type="range" min={1} max={3} step={0.01} value={place.zoom} onChange={(e) => setPlace((p) => clamp(img, { ...p, zoom: Number(e.target.value) }))} />
              </label>
              <p className="max-w-[270px] text-xs text-muted">Drag to move it. The frame is the reader's screen.</p>
            </div>
          ) : gif ? (
            <img src={gifUrl ?? undefined} alt="New reader background" className="aspect-[9/16] w-[270px] rounded-xl border border-line object-cover" />
          ) : current.data?.url ? (
            <img src={current.data.url} alt="Reader background" className="aspect-[9/16] w-[270px] rounded-xl border border-line object-cover" />
          ) : (
            <div className="grid aspect-[9/16] w-[270px] place-items-center rounded-xl border border-dashed border-line text-center text-sm text-muted">Stripe's own screen</div>
          )}
          <div className="flex max-w-sm flex-col gap-3 text-sm">
            <p className="text-ink-soft">A tall picture works best (portrait, like a phone). PNG or JPG is fitted to the reader's 1080 × 1920 screen here; a GIF is sent as it is (under 4 MB).</p>
            <label className="inline-flex w-fit cursor-pointer items-center rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 font-semibold hover:bg-mist">
              {current.data?.url || editing ? "Choose another picture" : "Choose a picture"}
              <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={(e) => (pick(e.target.files?.[0]), (e.target.value = ""))} />
            </label>
            {editing && (
              <div className="flex gap-2">
                <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
                  Send to the readers
                </Button>
                <Button variant="quiet" onClick={() => (setImg(null), setGif(null))}>
                  Cancel
                </Button>
              </div>
            )}
            {!editing && current.data?.url && (
              <Button variant="quiet" className="w-fit" busy={reset.isPending} onClick={() => reset.mutate()}>
                Use Stripe's own screen
              </Button>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}
